import { firestore, functions } from '@utils/firebase'
import { Org } from '@Interfaces/Data/OrganizationInterface'
import { StripeConnectAccount, StripeCustomerWithDetails } from '@Interfaces/Data/StripeInterface'
import { ApiResponse } from '@Interfaces/Api/ResponsesInterface'

/**
 * Get Stripe Connect account status
 */
export const getStripeConnectAccount = async (
  org: Org
): Promise<ApiResponse<StripeConnectAccount | null>> => {
  try {
    const stringifiedOrg = JSON.stringify(org)
    const res = await functions.httpsCallable('getAccountStatusV2')(stringifiedOrg)

    return {
      success: true,
      data: res.data || null,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get Stripe Connect account',
      data: undefined,
    }
  }
}

/**
 * Get Stripe customer and payment methods
 */
export const getStripeCustomerAndPaymentMethods = async (
  stripeCustomerId: string
): Promise<ApiResponse<StripeCustomerWithDetails>> => {
  try {
    const res = await functions.httpsCallable('getPaymentMethodsV2')({
      stripeCustomerId,
      isCompany: true,
    })

    return {
      success: true,
      data: {
        customer: res.data.customer,
        paymentMethods: res.data.paymentMethods,
        subscriptions: res.data.subscriptions,
        upcomingInvoice: res.data.upcomingInvoice,
      },
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get payment methods',
      data: undefined,
    }
  }
}

/**
 * Delete payment method
 */
export const deletePaymentMethod = async (
  paymentMethodId: string
): Promise<ApiResponse<boolean>> => {
  try {
    await functions.httpsCallable('deletePaymentMethodV2')({ paymentMethodId })

    return {
      success: true,
      data: true,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to delete payment method',
      data: undefined,
    }
  }
}

/**
 * Update default payment method
 */
export const updateDefaultPaymentMethod = async (
  paymentMethodId: string,
  stripeCustomerId: string
): Promise<ApiResponse<boolean>> => {
  try {
    await functions.httpsCallable('updateDefaultPaymentMethodV2')({
      paymentMethodId,
      stripeCustomerId,
    })

    return {
      success: true,
      data: true,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to update default payment method',
      data: undefined,
    }
  }
}

/**
 * Update subscription payment method
 */
export const updateSubscriptionPaymentMethod = async (
  subscriptionId: string,
  paymentMethodId: string
): Promise<ApiResponse<any>> => {
  try {
    const res = await functions.httpsCallable('updateSubscriptionPaymentMethodV2')({
      subscriptionId,
      paymentMethodId,
    })

    return {
      success: true,
      data: res.data,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to update subscription payment method',
      data: undefined,
    }
  }
}

/**
 * Update business account subscription
 */
export const updateBusinessAccountSubscription = async (
  subscriptionId: string,
  amount: number
): Promise<ApiResponse<any>> => {
  try {
    const res = await functions.httpsCallable('updateBusinessAccountSubscriptionV2')({
      subscriptionId,
      amount,
    })

    return {
      success: true,
      data: res.data,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to update subscription',
      data: undefined,
    }
  }
}
